import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useLeague } from '../utils/LeagueContext';
import { useUser } from '../UserContext';
import useUserRole from '../hooks/useUserRole';
import PrizeNavigation from '../components/PrizeNavigation';
import NoLeagueMessage from '../components/NoLeagueMessage';
import useLeagueAccessGuard from '../hooks/useLeagueAccessGuard';
import styles from './LeagueBuzz.module.css';
import API_BASE_URL from '../utils/apiConfig';

const FORMAT_LABELS = {
  press_conference: '🎙️ Press Conference',
  insider_report: '📰 Insider Report',
  power_rankings: '📊 Power Rankings',
  trade_rumors: '🔄 Trade Rumors',
  commissioners_corner: "📋 Commissioner's Corner",
};

const LeagueBuzz = () => {
  const { user } = useUser();
  const { selectedLeague } = useLeague();
  const { leagueId: leagueIdFromURL } = useParams();
  const leagueId = selectedLeague ? selectedLeague._id : leagueIdFromURL || null;
  const userRole = useUserRole(selectedLeague);
  useLeagueAccessGuard();

  const [buzzPosts, setBuzzPosts] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [highlightedOverride, setHighlightedOverride] = useState(null);
  const [commissionerNote, setCommissionerNote] = useState('');
  const [copied, setCopied] = useState(false);
  const [showAllPosts, setShowAllPosts] = useState(false);
  const [showLeagueData, setShowLeagueData] = useState(false);
  const [leagueData, setLeagueData] = useState({
    weeklyPrizeAmount: '',
    eosFirstPlace: '',
    weeklyWinners: '',
    standings: '',
    lastWeekResults: '',
    leagueHistory: '',
  });

  const fetchData = useCallback(async () => {
    if (!leagueId || !user?.token) return;

    setLoading(true);
    setError(null);

    try {
      const [defaultsRes, buzzRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/leagues/${leagueId}/buzz/defaults`, {
          headers: { Authorization: `Bearer ${user.token}` },
          credentials: 'include',
        }),
        fetch(`${API_BASE_URL}/api/leagues/${leagueId}/buzz`, {
          headers: { Authorization: `Bearer ${user.token}` },
          credentials: 'include',
        }),
      ]);

      if (defaultsRes.ok) {
        const defaultsData = await defaultsRes.json();
        setDefaults(defaultsData);
      }

      if (buzzRes.ok) {
        const buzzData = await buzzRes.json();
        setBuzzPosts(buzzData);
      }
    } catch (err) {
      setError('Failed to load League Buzz. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [leagueId, user?.token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerate = async () => {
    if (!leagueId || !user?.token) return;

    setGenerating(true);
    setError(null);

    try {
      const body = {
        highlightedMemberName: highlightedOverride || defaults?.highlighted,
        commissionerNote,
        ...(showLeagueData ? { leagueData } : {}),
      };

      const res = await fetch(`${API_BASE_URL}/api/leagues/${leagueId}/buzz/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to generate buzz');
      }

      const { buzz } = await res.json();

      setBuzzPosts((prev) => {
        const filtered = prev.filter(
          (p) => !(p.season === buzz.season && p.week === buzz.week)
        );
        return [buzz, ...filtered];
      });

      setDefaults((prev) => prev ? { ...prev, alreadyGenerated: true } : prev);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleToggleBuzz = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/leagues/${leagueId}/buzz/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setDefaults((prev) => ({ ...prev, buzzEnabled: data.buzzEnabled }));
      }
    } catch (err) {
      console.error('Error toggling buzz:', err);
    }
  };

  const handleLeagueDataChange = (field, value) => {
    setLeagueData((prev) => ({ ...prev, [field]: value }));
  };

  const formatTimestamp = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      <div>
        <PrizeNavigation navigationButtonArea="/2813821-1@2x.png" leagueId={leagueId} />
      </div>

      <div className={styles.page}>
        {!selectedLeague && <NoLeagueMessage entity="buzz" />}

        {selectedLeague && (
          <>
            <div className={styles.header}>
              <h2 className={styles.leagueName}>{selectedLeague.leagueName}</h2>
              <h3 className={styles.title}>League Buzz</h3>
              <p className={styles.subtitle}>AI-generated weekly content for your league 🏈</p>
              <p className={styles.disclaimer}>
                Content is AI-generated, fictional, and intended to be good-humored. Not a representation of facts.
              </p>
            </div>

            {/* Commissioner controls */}
            {userRole === 'commissioner' && (
              <div className={styles.controls}>
                {defaults && (
                  <>
                    <div className={styles.buzzToggle}>
                      <span className={styles.buzzToggleLabel}>
                        League Buzz is {defaults?.buzzEnabled ? 'enabled ✅' : 'disabled ⏸️'}
                      </span>
                      <button
                        className={styles.buzzToggleButton}
                        onClick={handleToggleBuzz}
                      >
                        {defaults?.buzzEnabled ? 'Disable Buzz' : 'Enable Buzz'}
                      </button>
                    </div>

                    {defaults?.buzzEnabled && (
                      <>
                        <p className={styles.weekLabel}>
                          This week: <strong>Week {defaults.week}</strong> — {FORMAT_LABELS[defaults.format]}
                        </p>

                        <div className={styles.field}>
                          <label className={styles.label}>Featured manager</label>
                          <select
                            className={styles.select}
                            value={highlightedOverride || defaults.highlighted || ''}
                            onChange={(e) => setHighlightedOverride(e.target.value)}
                          >
                            {defaults.memberNames.map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                        </div>

                        <div className={styles.field}>
                          <label className={styles.label}>Commissioner's note <span className={styles.optional}>(optional)</span></label>
                          <input
                            className={styles.input}
                            type="text"
                            placeholder="e.g. Don't forget dues are due Friday!"
                            value={commissionerNote}
                            onChange={(e) => setCommissionerNote(e.target.value)}
                          />
                        </div>

                        <div className={styles.field}>
                          <button
                            className={styles.enrichToggle}
                            onClick={() => setShowLeagueData((prev) => !prev)}
                            type="button"
                          >
                            {showLeagueData ? '▲ Hide' : '▼ Add league data to enrich content'} <span className={styles.optional}>(optional)</span>
                          </button>

                          {showLeagueData && (
                            <div className={styles.enrichFields}>
                              {[
                                { key: 'weeklyPrizeAmount', label: 'Weekly prize amount', placeholder: 'e.g. $25' },
                                { key: 'eosFirstPlace', label: 'End of season 1st place prize', placeholder: 'e.g. $200' },
                                { key: 'weeklyWinners', label: 'Weekly prize winners so far', placeholder: 'e.g. Sarathi (Wk1), Jake (Wk2)' },
                                { key: 'standings', label: 'Current standings', placeholder: 'e.g. 1. Sarathi 4-1, 2. Jake 3-2' },
                                { key: 'lastWeekResults', label: "Last week's results", placeholder: 'e.g. Sarathi def. Jake 142-98' },
                                { key: 'leagueHistory', label: 'League history / lore', placeholder: 'e.g. Jake is a 2x champion' },
                              ].map(({ key, label, placeholder }) => (
                                <div key={key} className={styles.enrichField}>
                                  <label className={styles.label}>{label}</label>
                                  <input
                                    className={styles.input}
                                    type="text"
                                    placeholder={placeholder}
                                    value={leagueData[key]}
                                    onChange={(e) => handleLeagueDataChange(key, e.target.value)}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {error && <p className={styles.error}>{error}</p>}

                        <button
                          className={styles.generateButton}
                          onClick={handleGenerate}
                          disabled={generating}
                        >
                          {generating
                            ? 'Generating...'
                            : defaults.alreadyGenerated
                            ? '🔄 Regenerate Buzz'
                            : '✨ Generate Buzz'}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Buzz feed */}
            <div className={styles.feed}>
              {loading ? (
                <p className={styles.emptyState}>Loading...</p>
              ) : buzzPosts.length === 0 ? (
                <>
                  {!defaults?.buzzEnabled && buzzPosts.length === 0 && userRole === 'commissioner' && (
                    <p className={styles.emptyState}>Enable League Buzz above to start generating weekly content for your league.</p>
                  )}
                  {(defaults?.buzzEnabled || userRole !== 'commissioner') && (
                    <p className={styles.emptyState}>
                      {userRole === 'commissioner'
                        ? 'Generate your first League Buzz above!'
                        : 'No buzz yet this week. Check back soon!'}
                    </p>
                  )}
                </>
              ) : (
                (() => {
                  const visiblePosts = showAllPosts ? buzzPosts : buzzPosts.slice(0, 3);
                  return (
                    <>
                      {visiblePosts.map((post) => (
                        <div key={post._id} className={styles.buzzCard}>
                          <div className={styles.buzzMeta}>
                            <span className={styles.weekBadge}>Week {post.week}</span>
                            <span className={styles.formatLabel}>{FORMAT_LABELS[post.format] || post.format}</span>
                            <span className={styles.timestamp}>{formatTimestamp(post.createdAt)}</span>
                          </div>
                          <pre className={styles.buzzContent}>{post.content}</pre>
                          <button
                            className={styles.copyButton}
                            onClick={() => handleCopy(post.content)}
                          >
                            {copied ? '✅ Copied!' : '📋 Copy'}
                          </button>
                        </div>
                      ))}
                      {buzzPosts.length > 3 && (
                        <button
                          className={styles.showMoreButton}
                          onClick={() => setShowAllPosts(!showAllPosts)}
                        >
                          {showAllPosts ? 'Show less' : `Show ${buzzPosts.length - 3} older posts`}
                        </button>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default LeagueBuzz;
